import {useEffect} from 'react';
import {getSnapshot, subscribe} from '@hooks/dockStore';

const useDockedTable = ({
							tableSelector,
							buffer = 8,
							offsetPx = 2,
						} = {}) => {
	useEffect(() => {
		if (!tableSelector) return;

		const clonedTables = new Map(); // tableEl -> {clone, wrap}
		let rafId = null;
		// One reusable, full-viewport-width WHITE bar that sits behind the (narrow,
		// centered) table clone while docked. It does two jobs: (a) full-bleed white
		// backing so the table body scrolling underneath can't peek ~1px beside the
		// clone (matching the full-bleed tabs bar above), and (b) casting the
		// bottom-only --sh-dock-shadow edge-to-edge. Created lazily, kept across
		// dock/undock so its shared --sh-header-shift transform stays continuous.
		let shadowBar = null;
		// Timestamp of the last frame a table clone was docked. Used to bridge the
		// brief no-clone gap when scrolling from one table straight into the next, so
		// the shadow doesn't flash up onto the tabs and back down during the handoff.
		let lastCloneVisibleMs = -Infinity;
		const CLONE_HANDOFF_GRACE_MS = 160;

		const DOCK_TRANSITION =
		  'transform var(--sh-dock-duration, 0.3s) var(--sh-dock-ease, cubic-bezier(0.4, 0, 0.2, 1))';

		// Minimum bar height so the bottom-only --sh-dock-shadow (negative spread)
		// never collapses on a very short head. The actual height is set per-frame to
		// span the docked head (see handleScroll), so the white backing covers the
		// whole clone; this is just a floor.
		const MIN_BAR_HEIGHT_PX = 32;

		const ensureShadowBar = () => {
			if (shadowBar) return shadowBar;
			shadowBar = document.createElement('div');
			shadowBar.style.position = 'fixed';
			shadowBar.style.left = '0';
			shadowBar.style.width = '100vw';
			shadowBar.style.pointerEvents = 'none';
			// Full-bleed white backing behind the (narrow) clone.
			shadowBar.style.background = '#fff';
			// Below the clone (z 4) so the clone's real head paints on top of this
			// white backing, but above normal page content so it masks the table body
			// peeking beside the clone and the shadow falls onto what scrolls beneath.
			shadowBar.style.zIndex = '3';
			shadowBar.style.boxShadow = 'var(--sh-dock-shadow)';
			// Glide with the header/tabs/clone via the same shared transform.
			shadowBar.style.transform = 'translateY(var(--sh-header-shift, 0px))';
			shadowBar.style.transition = DOCK_TRANSITION;
			shadowBar.style.willChange = 'transform';
			shadowBar.style.backfaceVisibility = 'hidden';
			shadowBar.style.display = 'none';
			document.body.appendChild(shadowBar);
			return shadowBar;
		};
		// After a width change the wrap reflows over several frames (esp. in device
		// emulation): padding, centering and scrollbar all settle at slightly
		// different times. We keep re-measuring the horizontal box until this
		// timestamp so the FINAL settled layout always wins, instead of latching a
		// transient (e.g. no-padding / full-bleed) frame and never retrying.
		let hSettleUntil = 0;
		// Bounded post-window retry budget so a wrap that never reports a consistent
		// box can't spin requestAnimationFrame forever.
		let hRetries = 0;
		// The clone's horizontal box only changes on resize / when it first shows,
		// never during a vertical scroll. This flag lets us skip the expensive
		// wrap measurement (getBoundingClientRect + getComputedStyle) on every
		// scroll frame, so we don't force a synchronous layout while the dock
		// transform is animating (mobile stutter).
		let horizontalDirty = true;

		// Target dock offset (step value) used only to decide when to show/hide the
		// clone. The clone's vertical motion itself is handled by CSS (see ensureClone).
		const getFixedTop = () => getSnapshot().dockTop - offsetPx;

		// Read the tabs' current translateY so a freshly (re)created clone can start
		// its transition from the live position instead of snapping to the end.
		const getLiveShift = () => {
			const tabsEl = document.getElementById('tabsNavHolder');
			if (tabsEl) {
				const t = window.getComputedStyle(tabsEl).transform;
				if (t && t !== 'none') {
					try {
						return new DOMMatrixReadOnly(t).m42;
					} catch {
						return null;
					}
				}
			}
			return null;
		};

		const applyHorizontalSync = (entry) => {
			if (!entry || !entry.clone || !entry.wrap) return;
			const {clone, wrap} = entry;
			const innerThead = clone.querySelector('thead');
			if (innerThead) {
				innerThead.style.transform = `translateX(-${wrap.scrollLeft}px)`;
			}
		};

		const ensureClone = (tableEl) => {
			if (clonedTables.has(tableEl)) {
				return clonedTables.get(tableEl);
			}

			const wrap = tableEl.parentElement;
			if (!wrap) return null;

			const thead = tableEl.querySelector('thead');
			if (!thead) return null;

			const clone = tableEl.cloneNode(false);
			const theadClone = thead.cloneNode(true);
			clone.appendChild(theadClone);

			clone.style.position = 'fixed';
			clone.style.zIndex = '4';
			clone.style.background = '#fff';
			clone.style.pointerEvents = 'none';
			// The clone itself does NOT carry the drop-shadow: it's narrow/centered
			// (matches the table content), so a shadow on it would stop short of the
			// viewport edges. Instead a separate full-bleed shadow bar is cast at the
			// clone's bottom (see ensureShadowBar / handleScroll) so the docked table
			// shadow spans edge-to-edge, matching the full-bleed tabs bar above it.
			clone.className = tableEl.className;
			clone.style.overflowX = 'hidden';
			clone.style.display = 'none';
			clone.style.margin = '0';
			clone.style.padding = '0';
			// Vertical: static base position (below the shown header + tabs) plus the
			// SAME compositor transform as the header/tabs. Driving it off the shared
			// --sh-header-shift variable puts the clone on the exact same CSS
			// transition, so it stays glued to them with no main-thread lag.
			clone.style.top =
			  `calc(var(--sh-header-height, 0px) + var(--sh-tabs-height, 0px) - ${offsetPx}px)`;
			clone.style.transform = 'translateY(var(--sh-header-shift, 0px))';
			clone.style.transition =
			  'transform var(--sh-dock-duration, 0.3s) var(--sh-dock-ease, cubic-bezier(0.4, 0, 0.2, 1))';
			// Permanent compositor layer -> the hide/show transform stays glued to
			// the header/tabs without a re-rasterisation flash on mobile.
			clone.style.willChange = 'transform';
			clone.style.backfaceVisibility = 'hidden';

			const theadCloneEl = clone.querySelector('thead');
			if (theadCloneEl) {
				theadCloneEl.style.display = 'table';
				theadCloneEl.style.borderBottom = 'none';
			}

			// Drop the header's bottom border on the clone: it sits right where the
			// docked shadow is cast, so the two would overlap and look doubled. The
			// shadow now provides that visual edge instead.
			clone.style.borderBottom = 'none';
			clone.querySelectorAll('thead th, thead td, thead tr').forEach((cell) => {
				cell.style.borderBottom = 'none';
			});

			document.body.appendChild(clone);

			const entry = {clone, wrap};
			clonedTables.set(tableEl, entry);

			const syncScroll = () => applyHorizontalSync(entry);
			wrap.addEventListener('scroll', syncScroll);
			clone._removeSyncScroll = () => {
				wrap.removeEventListener('scroll', syncScroll);
			};

			return entry;
		};

		const handleScroll = () => {
			const tables = document.querySelectorAll(tableSelector);
			if (!tables.length) return;

			// Drop clones whose original table was removed from the DOM (the table
			// subtree can be remounted on re-render), so we don't leak or leave a
			// stale header behind.
			clonedTables.forEach((entry, tableEl) => {
				if (!tableEl.isConnected) {
					if (entry.clone) {
						if (entry.clone._removeSyncScroll) entry.clone._removeSyncScroll();
						entry.clone.remove();
					}
					clonedTables.delete(tableEl);
				}
			});

			const fixedTop = getFixedTop();
			const scrollTop = window.scrollY || document.documentElement.scrollTop;
			const nowMs =
			  typeof performance !== 'undefined' ? performance.now() : Date.now();

			// Turns false if any shown clone needed a horizontal (re)measure this
			// frame but the wrap was mid-reflow (inconsistent box). We then keep
			// horizontalDirty set so the next frame retries, instead of stranding the
			// clone at a stale box (this is what broke device-emulation resizes: the
			// docked header stayed at its old desktop position, shoved to the side).
			let hBoxSettled = true;

			tables.forEach((tableEl, tableIdx) => {
				const tableRect = tableEl.getBoundingClientRect();

				const thead = tableEl.querySelector('thead');
				if (!thead) return;

				const theadHeight = thead.offsetHeight;

				const entry = clonedTables.get(tableEl);

				const shownNow =
				  !!(entry && entry.clone && entry.clone.style.display !== 'none');

				// Skip tables with no real body under the head: docking a head-only
				// table just pops it into place with nothing scrolling behind it.
				if (tableRect.height <= theadHeight + 1) {
					if (shownNow && entry) {
						entry.clone.style.display = 'none';
					}
					return;
				}

				// Two dock lines: tabs-only (menu hidden) and header+tabs (menu
				// shown). The clone's VISUAL position glides between them via the
				// shared --sh-header-shift transform, but the numeric dockTop JUMPS
				// by headerHeight at the hide/show step. Gating show/hide off that
				// jumping line made a menu hide/show near a table edge flip the clone
				// off then on -> flicker. So we gate with a hysteresis band across
				// both lines.
				const snap = getSnapshot();
				const minLine = (snap.tabsHeight || 0) - offsetPx; // menu hidden
				const maxLine =
				  (snap.headerHeight || 0) + (snap.tabsHeight || 0) - offsetPx; // menu shown

				const theadTop = tableRect.top;
				const tableBot = tableRect.bottom;

				// ENTER exactly at the (current) dock line: the docked tabs bar is
				// covering the real head at that same spot, so the swap is invisible
				// (no gap frame, no overshoot flash) and each table cleanly replaces
				// the previous one on the way down.
				// STAY until the head comes back down past the MAX (menu-shown) line
				// -> a menu hide/show can't flip it -> no flicker; and only while the
				// table still spans the dock zone.
				const shouldShowClone = shownNow
				  ? (theadTop <= maxLine + buffer && tableBot > minLine)
				  : (theadTop <= fixedTop && tableBot > fixedTop + buffer);

				if (shouldShowClone) {
					const ensuredEntry = ensureClone(tableEl);
					if (!ensuredEntry) return;

					const {clone, wrap} = ensuredEntry;
					const isCloneVisible = clone.style.display !== 'none';

					// Remember the docked head height so the full-bleed shadow bar can
					// be placed at this clone's bottom edge (see post-loop block).
					ensuredEntry.theadHeight = theadHeight;

					// Horizontal alignment with the (centered) wrap.
					//
					// The clone is a viewport-fixed overlay of the wrap's visible content
					// box: `left` is the wrap's content-left and `width` its content-width
					// (so it stays centred and clips an overflowing, horizontally
					// scrollable table), and the inner thead is translated by
					// wrap.scrollLeft elsewhere.
					//
					// We re-measure EVERY frame while shown: the box can move without a
					// window resize (lazy-loaded tab content reflowing, a scrollbar
					// appearing, the centered layout shifting), and measuring once per
					// show latched different tables at different stale offsets (some right,
					// some left). getBoundingClientRect is cheap (we already read the
					// table's rect); only getComputedStyle(padding) is costly, so it stays
					// cached and is refreshed on resize or when a frame looks mid-reflow.
					//
					// `left` is derived from the TABLE's own content edge
					// (tableRect.left + scrollLeft), which is padding-independent, so it's
					// correct even if the cached padding is briefly stale. A consistency
					// check vs the wrap's padded left detects a mid-reflow frame (e.g. the
					// transient full-bleed remount) and skips it, keeping the last good box.
					{
						let measured = false;
						const wrapRect = wrap.getBoundingClientRect();
						if (wrapRect.width > 0 && tableRect.width > 0) {
							if (ensuredEntry.padL == null || horizontalDirty) {
								const wrapStyle = window.getComputedStyle(wrap);
								ensuredEntry.padL = parseFloat(wrapStyle.paddingLeft) || 0;
								ensuredEntry.padR = parseFloat(wrapStyle.paddingRight) || 0;
							}
							const contentLeft = tableRect.left + wrap.scrollLeft;
							const wrapContentLeft = wrapRect.left + ensuredEntry.padL;
							const consistent =
							  Math.abs(wrapContentLeft - contentLeft) <= 2;
							const innerWidth = Math.round(
							  wrapRect.width - ensuredEntry.padL - ensuredEntry.padR
							);
							if (consistent && innerWidth > 0) {
								ensuredEntry.left = Math.round(contentLeft);
								ensuredEntry.width = innerWidth;
								measured = true;
							} else {
								// Padding cache may have been read during a mid-reflow frame
								// -> drop it so it's re-read next frame.
								ensuredEntry.padL = null;
							}
						}
						// Mid-reflow this frame: keep retrying so we don't leave the clone
						// at a stale box.
						if (!measured) hBoxSettled = false;
					}

					// Without a valid box, don't reveal the clone at a guessed
					// position (would flash to the left).
					if (ensuredEntry.left == null) return;

					// Copy the original column widths onto the clone. Done on first
					// show and again after a width resize (e.g. mobile->desktop), so
					// the docked header re-expands to the new column widths instead of
					// staying frozen at the previous (narrower) layout.
					if (!isCloneVisible || horizontalDirty) {
						const originalThs = tableEl.querySelectorAll('thead th');
						const clonedThs = clone.querySelectorAll('thead th');
						let totalWidth = 0;

						originalThs.forEach((th, i) => {
							if (clonedThs[i]) {
								const width = th.offsetWidth;
								totalWidth += width;
								clonedThs[i].style.width = `${width}px`;
								clonedThs[i].style.minWidth = `${width}px`;
								clonedThs[i].style.maxWidth = `${width}px`;
							}
						});

						const theadCloneEl = clone.querySelector('thead');
						if (theadCloneEl) {
							theadCloneEl.style.width = `${totalWidth}px`;
						}

						applyHorizontalSync(ensuredEntry);
					}

					// Only touch the (layout) left/width when they actually change.
					// During a vertical scroll the horizontal box is constant, so
					// rewriting these every rAF would force a layout + recomposite on
					// each frame and make the compositor-driven header-hide transform
					// transition hitch (visible flicker on mobile).
					if (
					  ensuredEntry.left != null &&
					  (ensuredEntry.left !== ensuredEntry.appliedLeft ||
						ensuredEntry.width !== ensuredEntry.appliedWidth)
					) {
						clone.style.left = `${ensuredEntry.left}px`;
						clone.style.width = `${ensuredEntry.width}px`;
						ensuredEntry.appliedLeft = ensuredEntry.left;
						ensuredEntry.appliedWidth = ensuredEntry.width;
					}

					clone.style.display = '';

					// If the clone was just (re)created while a dock transition is in
					// flight, seed its transform from the tabs' current position so it
					// eases in alongside them instead of snapping to the end.
					if (!isCloneVisible) {
						const liveShift = getLiveShift();
						if (liveShift != null) {
							clone.style.transition = 'none';
							clone.style.transform = `translateY(${liveShift}px)`;
							void clone.offsetHeight; // force reflow to lock the start
							clone.style.transition =
							  'transform var(--sh-dock-duration, 0.3s) var(--sh-dock-ease, cubic-bezier(0.4, 0, 0.2, 1))';
							clone.style.transform = 'translateY(var(--sh-header-shift, 0px))';
						}
					}

				} else if (entry && entry.clone.style.display !== 'none') {
					entry.clone.style.display = 'none';
				}
			});

			// Move the drop-shadow to the last docked element: while any table clone
			// is docked it becomes the bottom of the stack, so we (a) suppress the
			// tabs' shadow and (b) cast the shadow from a full-viewport-width bar at
			// that clone's bottom edge (the clone itself is narrow/centered, so its
			// shadow would stop short of the viewport edges). When no clone is docked
			// we hide the bar and let the tabs' .tabsNavFixed shadow show again.
			let visibleEntry = null;
			clonedTables.forEach((entry) => {
				if (entry.clone && entry.clone.style.display !== 'none') {
					visibleEntry = entry;
				}
			});

			const tabsEl = document.getElementById('tabsNavHolder');

			if (visibleEntry) {
				lastCloneVisibleMs = nowMs;
				if (tabsEl) tabsEl.style.boxShadow = 'none';
				const bar = ensureShadowBar();
				const theadH = visibleEntry.theadHeight || 0;
				// Span the bar over the docked head (floored at MIN_BAR_HEIGHT_PX so the
				// shadow can't collapse) and align its BOTTOM to the clone's bottom:
				// clone base top (header + tabs heights, via the live CSS vars) + head
				// height, minus the bar's own height. The shared --sh-header-shift
				// transform then glides it in lockstep with the header/tabs/clone.
				const barH = Math.max(theadH, MIN_BAR_HEIGHT_PX);
				const barTop =
				  `calc(var(--sh-header-height, 0px) + var(--sh-tabs-height, 0px) - ${offsetPx}px + ${theadH}px - ${barH}px)`;
				if (bar._appliedTop !== barTop) {
					bar.style.top = barTop;
					bar._appliedTop = barTop;
				}
				if (bar._appliedHeight !== barH) {
					bar.style.height = `${barH}px`;
					bar._appliedHeight = barH;
				}
				bar.style.display = '';
			} else if (nowMs - lastCloneVisibleMs < CLONE_HANDOFF_GRACE_MS) {
				// Handing off between two adjacent tables: hold the previous state for
				// a short grace window (tabs shadow suppressed, bar left where it was)
				// so the shadow doesn't flash onto the tabs and back. Keep re-checking
				// each frame until the window elapses (a parked scroll fires no events).
				if (tabsEl) tabsEl.style.boxShadow = 'none';
				schedule();
			} else {
				// Truly clear of all tables: let the tabs' own shadow show again and
				// hide the bar.
				if (tabsEl) tabsEl.style.boxShadow = '';
				if (shadowBar) shadowBar.style.display = 'none';
			}

			// While inside the post-resize settle window, keep re-measuring every
			// frame (dirty stays set) and self-schedule the next frame, since a
			// resize fires no further events on its own. This guarantees we capture
			// the FINAL settled layout and overwrite any transient (no-padding /
			// full-bleed / stale-desktop) box that a single mid-reflow frame may
			// have latched.
			if (nowMs < hSettleUntil) {
				horizontalDirty = true;
				hRetries = 0;
				schedule();
			} else if (!hBoxSettled && hRetries++ < 60) {
				// Out of the window but a shown clone still lacks a consistent box:
				// keep retrying (bounded) so it can't stay stranded.
				schedule();
			} else {
				horizontalDirty = false;
				hRetries = 0;
			}
		};

		const schedule = () => {
			if (rafId) return;
			rafId = requestAnimationFrame(() => {
				rafId = null;
				handleScroll();
			});
		};

		// Re-run when the shared dock state changes (header shows/hides, tabs
		// dock/undock) and on scroll/resize. The clone's vertical motion is handled
		// entirely by CSS (compositor), so we only need to toggle its visibility and
		// keep its horizontal alignment here.
		// Only a viewport WIDTH change can move the (centered) wrap horizontally.
		// On mobile the browser fires `resize` on every address-bar show/hide,
		// which only changes the height. Re-measuring on those height-only events
		// caught a transient reflow and shoved the docked clone to the left (and it
		// stayed until the next resize). So we re-measure horizontally only when
		// the width actually changes.
		let lastWidth = window.innerWidth;
		const onResize = () => {
			if (window.innerWidth !== lastWidth) {
				lastWidth = window.innerWidth;
				horizontalDirty = true;
				hRetries = 0;
				// Re-measure across the whole reflow settle period so the final
				// layout wins (device-emulation switches reflow over several frames).
				hSettleUntil =
				  (typeof performance !== 'undefined' ? performance.now() : Date.now()) + 400;
			}
			schedule();
		};

		const unsubscribe = subscribe(schedule);
		window.addEventListener('scroll', schedule, {passive: true});
		window.addEventListener('resize', onResize);
		handleScroll();

		return () => {
			unsubscribe();
			window.removeEventListener('scroll', schedule);
			window.removeEventListener('resize', onResize);
			if (rafId) cancelAnimationFrame(rafId);
			// Clear the shadow-suppression override so the tabs' own shadow isn't
			// left disabled if we unmounted while a clone was docked.
			const tabsEl = document.getElementById('tabsNavHolder');
			if (tabsEl) tabsEl.style.boxShadow = '';
			if (shadowBar) {
				shadowBar.remove();
				shadowBar = null;
			}
			clonedTables.forEach((entry) => {
				if (entry.clone) {
					if (entry.clone._removeSyncScroll) entry.clone._removeSyncScroll();
					entry.clone.remove();
				}
			});
			clonedTables.clear();
		};
	}, [tableSelector, buffer, offsetPx]);

	return null;
};

export default useDockedTable;